import { useState, useEffect, useCallback } from 'react';
import { useInjectedService } from './useInjectedService';

/**
 * CookieConsent Interface
 * Defines the structure for tracking user consent preferences.
 */
export interface CookieConsent {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
    timestamp: string;
}

const COOKIE_CONSENT_KEY = 'cookie_consent_preferences';

/**
 * useCookieConsent Hook - Platform Agnostic
 * 
 * Manages user consent preferences for storage and tracking.
 * Consumes the injected IStorageService for multi-platform persistence.
 * Replaces direct localStorage calls.
 */
export const useCookieConsent = () => {
    const storage = useInjectedService('storage');
    const [consent, setConsent] = useState<CookieConsent | null>(null);
    const [showBanner, setShowBanner] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        let isMounted = true;

        storage.get<CookieConsent>(COOKIE_CONSENT_KEY).then((storedConsent) => {
            if (!isMounted) return;

            if (storedConsent) {
                setConsent(storedConsent);
            } else {
                setShowBanner(true);
            }
            setIsLoaded(true);
        }).catch(() => {
            if (isMounted) {
                setShowBanner(true);
                setIsLoaded(true);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [storage]);

    const saveConsent = useCallback((preferences: Omit<CookieConsent, 'timestamp'>) => {
        const newConsent: CookieConsent = {
            ...preferences,
            timestamp: new Date().toISOString(),
        };
        setConsent(newConsent);
        storage.save(COOKIE_CONSENT_KEY, newConsent).catch((err: Error) => {
            console.error('Failed to persist consent', err);
        });
        setShowBanner(false);
    }, [storage]);

    const acceptAll = () => {
        saveConsent({
            necessary: true,
            analytics: true,
            marketing: true,
        });
    };

    const rejectAll = () => {
        saveConsent({
            necessary: true,
            analytics: false,
            marketing: false,
        });
    };

    return {
        consent,
        showBanner,
        isLoaded,
        saveConsent,
        acceptAll,
        rejectAll,
    };
};
